import { useState } from 'react';
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  XCircleIcon,
  MinusCircleIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';
import { useIntegrations } from '../hooks/useIntegrations';

// Each verdict carries its own colour, icon and plain-English meaning. The
// wording matters as much as the colour: "Overdue" alone doesn't tell you
// whether to care, so the row also spells out the consequence from the
// registry ("People data goes stale and leaver detection stops").
// `card` is the whole point of the colour system: a thick left rail plus a
// tinted border, so the state of every integration reads from across the room
// before a single word is parsed. Colour is never the ONLY signal though —
// each state also carries its own icon and a written label, so the screen still
// works for a colour-blind reader and in a screenshot.
const VERDICTS = {
  ok: {
    label: 'Healthy', icon: CheckCircleIcon, text: 'text-emerald-700',
    chip: 'bg-emerald-100 text-emerald-800 ring-emerald-600/30',
    card: 'border-emerald-300 border-l-emerald-500 bg-emerald-50/30',
  },
  overdue: {
    label: 'Overdue — not running', icon: ExclamationTriangleIcon, text: 'text-rose-700',
    chip: 'bg-rose-100 text-rose-800 ring-rose-600/30',
    card: 'border-rose-300 border-l-rose-500 bg-rose-50/40',
  },
  degraded: {
    label: 'Last run failed', icon: ExclamationTriangleIcon, text: 'text-amber-700',
    chip: 'bg-amber-100 text-amber-900 ring-amber-600/30',
    card: 'border-amber-300 border-l-amber-500 bg-amber-50/40',
  },
  failed: {
    label: 'Failing', icon: XCircleIcon, text: 'text-rose-700',
    chip: 'bg-rose-100 text-rose-800 ring-rose-600/30',
    card: 'border-rose-300 border-l-rose-500 bg-rose-50/40',
  },
  running: {
    label: 'Running now', icon: ArrowPathIcon, text: 'text-sky-700',
    chip: 'bg-sky-100 text-sky-800 ring-sky-600/30',
    card: 'border-sky-300 border-l-sky-500 bg-sky-50/30',
  },
  'never-run': {
    label: 'Never run', icon: MinusCircleIcon, text: 'text-gray-500',
    chip: 'bg-gray-100 text-gray-700 ring-gray-500/30',
    card: 'border-gray-200 border-l-gray-300 bg-white',
  },
};

const CHANGE_STYLES = {
  added: { sign: '+', className: 'text-emerald-700' },
  removed: { sign: '−', className: 'text-rose-700' },
  changed: { sign: '~', className: 'text-amber-700' },
};

function timeAgo(value) {
  if (!value) return 'never';
  const mins = Math.round((Date.now() - new Date(value).getTime()) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  return `${days}d ago`;
}

function formatWhen(value) {
  if (!value) return '—';
  return new Date(value).toLocaleString('en-GB', {
    weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
  });
}

function formatDuration(ms) {
  if (ms === null || ms === undefined) return '—';
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

// Explanations for the counts that reliably prompt a question. Anything not
// listed just renders as a plain pill — this is for the handful where the
// number alone is genuinely ambiguous.
const SUMMARY_HINTS = {
  notReconciled: 'Active in BrightHR but not in Nano — BrightHR test accounts (no Microsoft 365 identity) and anyone offboarded here who HR has not yet marked as a leaver in BrightHR. A small steady number is normal; a jump means the reconciliation broke.',
  pendingReview: 'People whose BrightHR and Microsoft 365 records could not be matched automatically and need a manual link.',
  hoursStale: 'How old the people data was when the watchdog checked it.',
  unmatched: 'Rows in the SharePoint list with no matching Nano person — mostly leavers.',
  matchedToPeople: 'Engineer directory rows successfully linked to a Nano person.',
};

function SummaryPills({ summary }) {
  if (!summary) return null;
  return (
    <div className="mt-2 flex flex-wrap gap-1.5">
      {Object.entries(summary).map(([key, value]) => {
        const hint = SUMMARY_HINTS[key];
        return (
          <span
            key={key}
            title={hint || undefined}
            className={`rounded px-1.5 py-0.5 text-xs text-gray-600 ${hint ? 'cursor-help bg-gray-100 decoration-dotted underline-offset-2 hover:underline' : 'bg-gray-100'}`}
          >
            {key.replace(/([A-Z])/g, ' $1').toLowerCase()}:{' '}
            <span className="font-medium tabular-nums text-gray-900">{String(value)}</span>
          </span>
        );
      })}
    </div>
  );
}

function Changes({ changes }) {
  if (!changes?.length) {
    return <p className="mt-2 text-xs text-gray-400">No changes picked up.</p>;
  }
  return (
    <ul className="mt-2 space-y-1">
      {changes.map((c, i) => {
        const style = CHANGE_STYLES[c.type] || CHANGE_STYLES.changed;
        return (
          <li key={i} className="flex gap-2 text-xs">
            <span className={`font-bold ${style.className}`}>{style.sign}</span>
            <span>
              <span className="font-medium text-gray-900">{c.label}</span>
              {c.detail ? <span className="text-gray-500"> — {c.detail}</span> : null}
            </span>
          </li>
        );
      })}
    </ul>
  );
}

function IntegrationCard({ integration }) {
  const [open, setOpen] = useState(false);
  const verdict = VERDICTS[integration.verdict] || VERDICTS['never-run'];
  const Icon = verdict.icon;
  const lastRun = integration.lastRun;

  return (
    <div className={`rounded-lg border-2 border-l-8 ${verdict.card}`}>
      <div className="flex flex-col gap-3 p-4 sm:flex-row sm:items-start">
        <Icon className={`h-6 w-6 shrink-0 ${verdict.text}`} />

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="font-medium text-gray-900">{integration.label}</h3>
            <span className={`rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${verdict.chip}`}>
              {verdict.label}
            </span>
            <span className="rounded bg-gray-100 px-1.5 py-0.5 text-xs text-gray-600">{integration.system}</span>
          </div>

          <p className="mt-1 text-sm text-gray-500">{integration.description}</p>

          {/* Only shown when something is actually wrong — on a healthy row it
              would be noise, but when it's overdue it's the reason to care. */}
          {['overdue', 'failed', 'degraded', 'never-run'].includes(integration.verdict) && integration.consequence ? (
            <p className={`mt-1 text-sm font-medium ${verdict.text}`}>{integration.consequence}</p>
          ) : null}

          {lastRun?.error ? (
            <p className="mt-2 rounded bg-rose-50 px-2 py-1 font-mono text-xs text-rose-700">{lastRun.error}</p>
          ) : null}

          <SummaryPills summary={lastRun?.summary} />

          {lastRun ? (
            <button
              type="button"
              onClick={() => setOpen((v) => !v)}
              className="mt-3 text-xs font-medium text-gray-500 underline-offset-2 hover:text-gray-900 hover:underline"
            >
              {open ? 'Hide' : 'Show'} what it picked up
              {lastRun.changes.length ? ` (${lastRun.changes.length})` : ''} and recent runs
            </button>
          ) : null}

          {open ? (
            <div className="mt-3 border-t border-gray-100 pt-3">
              <p className="text-xs font-medium uppercase tracking-wide text-gray-400">Last run picked up</p>
              <Changes changes={lastRun.changes} />

              <p className="mt-4 text-xs font-medium uppercase tracking-wide text-gray-400">Recent runs</p>
              <div className="mt-2 overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-left text-gray-400">
                      <th className="py-1 pr-4 font-medium">When</th>
                      <th className="py-1 pr-4 font-medium">Result</th>
                      <th className="py-1 pr-4 font-medium">Took</th>
                      <th className="py-1 pr-4 font-medium">Trigger</th>
                      <th className="py-1 font-medium">Changes</th>
                    </tr>
                  </thead>
                  <tbody className="text-gray-600">
                    {integration.recentRuns.map((r) => (
                      <tr key={r.id} className="border-t border-gray-100">
                        <td className="py-1 pr-4 whitespace-nowrap">{formatWhen(r.startedAt)}</td>
                        <td className={`py-1 pr-4 font-medium ${r.status === 'ok' ? 'text-emerald-700' : r.status === 'running' ? 'text-sky-700' : 'text-rose-700'}`}>
                          {r.status}
                          {r.attempt > 1 ? ` (attempt ${r.attempt})` : ''}
                        </td>
                        <td className="py-1 pr-4 tabular-nums">{formatDuration(r.durationMs)}</td>
                        <td className="py-1 pr-4">{r.trigger}</td>
                        <td className="py-1 tabular-nums">{r.changes.length || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : null}
        </div>

        <div className="shrink-0 text-left sm:text-right">
          <p className="text-sm font-medium text-gray-900">{timeAgo(integration.lastSuccessAt)}</p>
          <p className="text-xs text-gray-400">last success</p>
          <p className="mt-1 text-xs text-gray-400">Runs {integration.schedule}</p>
        </div>
      </div>
    </div>
  );
}

function Integrations() {
  const { data, isLoading, error, dataUpdatedAt } = useIntegrations();

  if (isLoading) {
    return <div className="p-8"><p className="text-sm text-gray-400">Loading integration health...</p></div>;
  }

  if (error) {
    const status = error.response?.status;
    return (
      <div className="p-8">
        <p className="text-sm text-rose-700">
          {status === 403
            ? 'The Platform section is restricted to full administrators.'
            : error.response?.data?.error || 'Could not load integration health.'}
        </p>
      </div>
    );
  }

  const integrations = data?.integrations || [];

  // Problems first, then healthy, then never-run. On a monitoring screen the
  // point is that problems find you rather than you scanning for them — and
  // never-run belongs at the bottom because it needs no action.
  const RANK = { failed: 0, overdue: 0, degraded: 1, running: 2, ok: 3, 'never-run': 4 };
  const ordered = [...integrations].sort((a, b) => (RANK[a.verdict] ?? 5) - (RANK[b.verdict] ?? 5));

  // 'never-run' is deliberately NOT counted as needing action — a weekly job
  // that hasn't come round yet is not a fault, and counting it would make the
  // banner red for a reason nobody can act on.
  const needsAction = integrations.filter((i) => ['failed', 'overdue', 'degraded'].includes(i.verdict)).length;
  const notYetRun = integrations.filter((i) => i.verdict === 'never-run').length;

  return (
    <div className="p-4 sm:p-8">
      <div className="mb-6">
        <h1 className="text-xl font-semibold text-gray-900">Integrations</h1>
        <p className="mt-1 text-sm text-gray-500">
          Every system Nano talks to on a schedule — when it last ran, whether it worked, and what it picked up.
        </p>
      </div>

      {/* Overall state, sized so it answers "is anything wrong?" from a glance
          at the top of the page — the question you'd otherwise answer by
          scanning every card. Counts only genuine problems: 'never run' is not
          a fault (a weekly job simply hasn't come round yet), so treating it as
          one would cry wolf every time an integration is added. */}
      <div
        className={`mb-5 flex items-center gap-3 rounded-lg border-2 border-l-8 p-4 ${
          needsAction ? VERDICTS.failed.card : VERDICTS.ok.card
        }`}
      >
        {needsAction ? (
          <ExclamationTriangleIcon className="h-8 w-8 shrink-0 text-rose-600" />
        ) : (
          <CheckCircleIcon className="h-8 w-8 shrink-0 text-emerald-600" />
        )}
        <div>
          <p className={`text-lg font-semibold ${needsAction ? 'text-rose-800' : 'text-emerald-800'}`}>
            {needsAction
              ? `${needsAction} integration${needsAction > 1 ? 's need' : ' needs'} attention`
              : 'All integrations healthy'}
          </p>
          <p className="text-xs text-gray-500">
            {notYetRun ? `${notYetRun} not yet run · ` : ''}
            Updated {timeAgo(dataUpdatedAt)} · refreshes automatically
          </p>
        </div>
      </div>

      <div className="space-y-3">
        {ordered.map((integration) => (
          <IntegrationCard key={integration.key} integration={integration} />
        ))}
      </div>
    </div>
  );
}

export default Integrations;
